import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { generateClient } from "aws-amplify/api";
import { ArrowLeft, LayoutGrid } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  getEvent,
  listCommanderPodPlayers,
  listCommanderPods,
  listCommanderRounds,
  listPlayerConstructs,
  listUsers,
} from "../graphql/queries";
import { getPlayerDisplayName } from "../lib/playerIdentity";

const client = generateClient();

const CommanderPods: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const loadList = async (query: string, field: string) => {
    const response: any = await client.graphql({
      query,
      variables: { filter: { eventId: { eq: eventId } }, limit: 1000 },
    });
    return response.data[field].items;
  };
  const { data: event } = useQuery({
    queryKey: ["event", eventId],
    queryFn: async () => {
      const response = await client.graphql({
        query: getEvent,
        variables: { id: eventId! },
      });
      return response.data.getEvent;
    },
  });
  const { data: rounds = [] } = useQuery({
    queryKey: ["commanderRounds", eventId],
    queryFn: () => loadList(listCommanderRounds, "listCommanderRounds"),
  });
  const { data: pods = [] } = useQuery({
    queryKey: ["commanderPods", eventId],
    queryFn: () => loadList(listCommanderPods, "listCommanderPods"),
  });
  const { data: players = [] } = useQuery({
    queryKey: ["commanderPodPlayers", eventId],
    queryFn: () =>
      loadList(listCommanderPodPlayers, "listCommanderPodPlayers"),
  });
  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await client.graphql({
        query: listUsers,
        variables: { limit: 1000 },
      });
      return response.data.listUsers.items;
    },
  });
  const { data: constructs = [] } = useQuery({
    queryKey: ["allPlayerConstructs"],
    queryFn: async () => {
      const response = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });
      return response.data.listPlayerConstructs.items;
    },
  });

  const visibleRounds = rounds
    .filter((round: any) => round.status !== "DRAFT")
    .sort((a: any, b: any) => b.roundNumber - a.roundNumber);
  const getName = (player: any) =>
    getPlayerDisplayName(player, users, constructs);

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" asChild>
          <Link to={`/event/${eventId}`}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Event
          </Link>
        </Button>
        <h1 className="mt-3 flex items-center gap-2 text-3xl font-bold">
          <LayoutGrid className="h-7 w-7 text-primary" />
          {event?.name || "Commander"} Pods
        </h1>
        <p className="text-muted-foreground">
          Published pairings and round results
        </p>
      </div>

      {visibleRounds.map((round: any) => (
        <section key={round.id} className="space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold">Round {round.roundNumber}</h2>
            <Badge>{round.status}</Badge>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {pods
              .filter((pod: any) => pod.roundId === round.id)
              .sort((a: any, b: any) => a.podNumber - b.podNumber)
              .map((pod: any) => (
                <Card key={pod.id}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      Pod {pod.podNumber}
                      <Badge variant="secondary">
                        {pod.category.toLowerCase()}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {players
                      .filter((player: any) => player.podId === pod.id)
                      .sort(
                        (a: any, b: any) =>
                          (a.placement || 99) - (b.placement || 99)
                      )
                      .map((player: any) => (
                        <div
                          key={player.id}
                          className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2"
                        >
                          <span>{getName(player)}</span>
                          <span className="text-sm text-muted-foreground">
                            {player.placement
                              ? `${player.placement} · ${player.points} pts`
                              : "Awaiting result"}
                          </span>
                        </div>
                      ))}
                  </CardContent>
                </Card>
              ))}
          </div>
        </section>
      ))}

      {visibleRounds.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Pairings have not been published yet.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CommanderPods;
